/**
 * @fileoverview Parcel data import subsection for the onboarding wizard Step 9.
 * @module system-web/pages/tenants/wizard/steps/ParcelImportSection
 *
 * Responsibilities:
 * - Offer three import paths (Regrid, Manual, Skip) for parcel data.
 * - Auto-match a Regrid master-cache file by jurisdiction.
 * - Configure token entitlements, spatial filtering, and fire-hazard levels.
 *
 * Dependencies:
 * - useParcelCacheFiles hook (Dexie-backed cache file list).
 * - @firesquirrel/core-domain hazard level labels.
 * - lucide-react icons.
 *
 * Architecture:
 * - Pure subsection component: receives parcelImport value and onChange callback.
 * - No API calls; data comes from the hook.
 *
 * Notes:
 * - Regrid file auto-match is FIPS-first (the 5-digit county FIPS is the only
 *   reliable join key), falling back to normalized state + county name for
 *   legacy drafts that predate `jurisdiction.fipsCode`.
 * - An explicit "Browse all files" selection always overrides the auto-match,
 *   and the summary reflects the effective file rather than the auto-match.
 * - Manual upload only captures the file name in wizard state; actual upload
 *   is handled post-provisioning.
 *
 * @localFirst
 * Source of Truth: `geo_cache_files` Dexie table for Regrid cache metadata.
 * Write Flow: user selections are written to the wizard state (Dexie) and
 *   persisted to the onboarding draft. Provisioning reads from the draft.
 * Sync Strategy: cache file metadata is fetched on demand by the hook.
 */
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  MapPin,
  Upload,
  AlertTriangle,
  CheckCircle,
  FileText,
  Minus,
  Plus,
  ChevronDown,
} from 'lucide-react';
import { HAZARD_LEVEL_LABELS, type FireHazardLevel } from '@firesquirrel/core-domain';
import type { WizardState, WizardParcelImport } from '../types';
import { useParcelCacheFiles } from '../../../../hooks/useParcelCacheFiles';

type ImportPath = WizardParcelImport['path'];
type FilterMode = NonNullable<WizardParcelImport['filterMode']>;

interface Props {
  state: WizardState;
  value: WizardParcelImport;
  onChange: (patch: Partial<WizardParcelImport>) => void;
}

/**
 * @process FormatBytes
 * Purpose: Render a human-readable byte size.
 * Inputs:  size in bytes.
 * Outputs: formatted string.
 */
function formatBytes(bytes?: number | null): string {
  if (bytes == null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * @component ParcelImportSection
 *
 * Purpose:
 * - Configure parcel data import as part of onboarding Step 9.
 *
 * Props:
 * - state:  Current wizard state (for jurisdiction + boundary context).
 * - value:  Current `parcelImport` value.
 * - onChange: Merge-style patch callback for `parcelImport`.
 *
 * Behavior:
 * - Shows three radio-card import paths.
 * - Auto-matches a Regrid cache file and lets the user browse all files.
 * - Exposes a token stepper and spatial filtering options for the Regrid path.
 *
 * Data Source:
 * - `useParcelCacheFiles` hook (Dexie + on-demand system-api refresh).
 *
 * Constraints:
 * - No direct API calls; no mock data; no hardcoded URLs.
 */
export default function ParcelImportSection({ state, value, onChange }: Props) {
  const jurisdiction = state.departmentInfo?.jurisdiction;
  const stateCode = jurisdiction?.state?.code;
  const stateName = jurisdiction?.state?.name;
  const countyName = jurisdiction?.county?.name;
  // The county lookup code IS the 5-digit state+county FIPS, so fall back to it
  // when a legacy draft has no explicit jurisdiction.fipsCode.
  const fipsCode = jurisdiction?.fipsCode
    ?? (/^\d{5}$/.test(jurisdiction?.county?.code ?? '') ? jurisdiction?.county?.code : undefined);

  const { files, loading: filesLoading, error, findAutoMatchedFile } = useParcelCacheFiles();

  const autoMatch = useMemo(
    () => findAutoMatchedFile(fipsCode, stateCode, countyName, stateName),
    [findAutoMatchedFile, fipsCode, stateCode, countyName, stateName],
  );

  // The file actually in effect: an explicit user choice from "Browse all
  // files" always wins over the jurisdiction auto-match. Rendering `autoMatch`
  // alone made a manual selection look like it never registered.
  const selectedFile = useMemo(
    () => (value.regridFileId ? files.find((f) => f._id === value.regridFileId) ?? null : null),
    [files, value.regridFileId],
  );
  const effectiveFile = selectedFile ?? autoMatch;
  const isManualOverride = Boolean(selectedFile && autoMatch && selectedFile._id !== autoMatch._id);

  // Department boundary availability — the 'department' and 'fireHazard'
  // spatial filter modes both rely on the department's boundary polygon
  // (retrieved from the NERIS lookup on Step 1 and persisted in
  // `departmentInfo.selectedBoundary`). Without it, those modes would
  // silently produce an empty or full-county import at provisioning time.
  const hasBoundary = Boolean(state.departmentInfo?.selectedBoundary);

  const [showBrowseAll, setShowBrowseAll] = useState(false);

  // Reset an invalid filter mode — if the admin previously selected a
  // boundary-dependent mode ('department' or 'fireHazard') but the boundary
  // is no longer available (e.g. it was never fetched, or the NERIS lookup
  // was redone and returned no polygon), fall back to 'all' so the wizard
  // never carries a filter selection that cannot be honored.
  useEffect(() => {
    if (value.path === 'skip') return;
    if (hasBoundary) return;
    if (value.filterMode === 'department' || value.filterMode === 'fireHazard') {
      onChange({ filterMode: 'all' });
    }
  }, [hasBoundary, value.path, value.filterMode, onChange]);

  // Ensure sensible defaults when the user switches to the Regrid path.
  useEffect(() => {
    if (value.path !== 'regrid') return;

    const patch: Partial<WizardParcelImport> = {};
    if (!value.tokensPerYear) patch.tokensPerYear = 1;
    if (!value.filterMode) {
      patch.filterMode = 'all';
      patch.hazardLevels = ['extreme'];
    }
    if (autoMatch && !value.regridFileId) {
      patch.regridFileId = autoMatch._id;
      patch.regridFileName = autoMatch.filename;
    }

    if (Object.keys(patch).length > 0) {
      onChange(patch);
    }
  }, [value.path, value.tokensPerYear, value.filterMode, value.regridFileId, value.hazardLevels, autoMatch, onChange]);

  const handleSelectPath = useCallback(
    (path: ImportPath) => {
      const base: Partial<WizardParcelImport> = { path };

      if (path === 'regrid') {
        base.tokensPerYear = value.tokensPerYear ?? 1;
        base.filterMode = value.filterMode ?? 'all';
        base.hazardLevels = value.hazardLevels ?? ['extreme'];
        // Contact creation is strictly opt-in — absence means off, matching the
        // provisioning route, the worker payload contract, and the
        // post-onboarding Parcel Data tab. `=== true` preserves an explicit
        // opt-in without ever inferring one.
        base.createOwnerContacts = value.createOwnerContacts === true;
        // Never clobber an explicit user selection when the path is re-picked.
        if (!value.regridFileId && autoMatch) {
          base.regridFileId = autoMatch._id;
          base.regridFileName = autoMatch.filename;
        }
      }

      if (path === 'manual') {
        base.filterMode = value.filterMode ?? 'all';
        base.hazardLevels = value.hazardLevels ?? ['extreme'];
      }

      if (path === 'skip') {
        setShowBrowseAll(false);
      }

      onChange(base);
    },
    [autoMatch, onChange, value.filterMode, value.hazardLevels, value.tokensPerYear, value.regridFileId, value.createOwnerContacts],
  );

  const handleSelectFile = useCallback(
    (file: { _id: string; filename: string; county: string; state: string }) => {
      onChange({
        regridFileId: file._id,
        regridFileName: file.filename,
      });
      setShowBrowseAll(false);
    },
    [onChange],
  );

  const adjustTokens = useCallback(
    (delta: number) => {
      const current = value.tokensPerYear ?? 1;
      const next = Math.max(1, Math.min(12, current + delta));
      onChange({ tokensPerYear: next });
    },
    [onChange, value.tokensPerYear],
  );

  const handleSetTokens = useCallback(
    (raw: string) => {
      const parsed = parseInt(raw, 10);
      const next = Number.isNaN(parsed) ? 1 : Math.max(1, Math.min(12, parsed));
      onChange({ tokensPerYear: next });
    },
    [onChange],
  );

  const handleFilterModeChange = useCallback(
    (mode: FilterMode) => {
      onChange({ filterMode: mode });
    },
    [onChange],
  );

  const handleOwnerContactsToggle = useCallback(
    (checked: boolean) => {
      onChange({ createOwnerContacts: checked });
    },
    [onChange],
  );

  const handleHazardToggle = useCallback(
    (level: FireHazardLevel) => {
      const current = value.hazardLevels ?? ['extreme'];
      const next = current.includes(level)
        ? current.filter((l) => l !== level)
        : [...current, level];
      onChange({ hazardLevels: next as FireHazardLevel[] });
    },
    [onChange, value.hazardLevels],
  );

  const handleManualFileSelect = useCallback(
    (file: File | undefined) => {
      onChange({ manualFileName: file ? file.name : undefined });
    },
    [onChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) handleManualFileSelect(file);
    },
    [handleManualFileSelect],
  );

  // Spatial filter section — shared by the Regrid and Manual paths. The
  // 'department' and 'fireHazard' modes both require the department boundary
  // polygon (from the Step 1 NERIS lookup); they are disabled and explained
  // when no boundary is present so the admin cannot select a filter that
  // would silently produce an empty or full-county import at provisioning.
  const renderSpatialFilter = (allParcelsHint: string, subtitle?: string) => {
    const options: Array<{ value: FilterMode; label: string; hint: string; requiresBoundary: boolean }> = [
      { value: 'all', label: 'All parcels', hint: allParcelsHint, requiresBoundary: false },
      { value: 'department', label: 'Department boundary', hint: 'Only parcels inside the department boundary', requiresBoundary: true },
      { value: 'fireHazard', label: 'Fire hazard overlay', hint: 'Only parcels in selected hazard zones', requiresBoundary: true },
    ];
    return (
      <div className="space-y-2">
        <div className="text-sm font-semibold text-gray-900">Spatial Filtering Mode</div>
        {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
        {!hasBoundary && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Department boundary not defined.</p>
              <p className="mt-0.5">
                The &ldquo;Department boundary&rdquo; and &ldquo;Fire hazard overlay&rdquo; filters require a
                department boundary polygon. Return to Step 1 (Department Info) and complete the NERIS lookup
                to retrieve the boundary, or choose &ldquo;All parcels&rdquo;.
              </p>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {options.map((opt) => {
            const disabled = opt.requiresBoundary && !hasBoundary;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => !disabled && handleFilterModeChange(opt.value)}
                disabled={disabled}
                aria-disabled={disabled}
                className={`border-2 rounded-lg px-3 py-2 text-left transition-colors ${
                  value.filterMode === opt.value
                    ? 'border-orange-500 bg-orange-50'
                    : disabled
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="text-xs font-semibold text-gray-900">{opt.label}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{opt.hint}</div>
              </button>
            );
          })}
        </div>
        {value.filterMode === 'fireHazard' && (
          <div className="p-3 border border-gray-200 rounded-lg bg-white">
            <div className="text-xs font-semibold text-gray-700 mb-2">Hazard levels to include</div>
            <div className="flex flex-wrap gap-4">
              {(['extreme', 'high', 'medium', 'low'] as const).map((level) => {
                const selected = (value.hazardLevels ?? ['extreme']).includes(level as FireHazardLevel);
                return (
                  <label key={level} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => handleHazardToggle(level as FireHazardLevel)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    {HAZARD_LEVEL_LABELS[level as FireHazardLevel]}
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPathCard = (
    path: ImportPath,
    icon: React.ReactNode,
    title: string,
    description: string,
    extra?: React.ReactNode,
  ) => {
    const selected = value.path === path;
    return (
      <label
        className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
          selected
            ? 'border-orange-500 bg-orange-50'
            : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <input
          type="radio"
          name="parcel-import-path"
          value={path}
          checked={selected}
          onChange={() => handleSelectPath(path)}
          className="mt-1 h-4 w-4 text-orange-600 border-gray-300 focus:ring-orange-500"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-sm font-semibold text-gray-900">{title}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          {extra}
        </div>
      </label>
    );
  };

  return (
    <div className="space-y-5">
      {/* Jurisdiction banner */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
        <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <span className="font-medium">Jurisdiction from Step 1:</span>{' '}
          {countyName && stateCode ? (
            <>
              {countyName}, {jurisdiction?.state?.name || stateCode}
              {fipsCode && <span className="text-blue-600 ml-1">(FIPS {fipsCode})</span>}
            </>
          ) : (
            'No jurisdiction selected. Return to Step 1 to choose a state and county.'
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Failed to load Regrid cache files: {error.message}</span>
        </div>
      )}

      {/* Import path cards */}
      <div className="space-y-3">
        {renderPathCard(
          'regrid',
          <CheckCircle className="h-4 w-4 text-orange-600" />,
          'Regrid Parcel Data (recommended)',
          'Auto-matched to your county. Includes 1 parcel data update per year at renewal. Additional updates can be purchased.',
          value.path === 'regrid' && (
            <div className="mt-2 text-xs">
              {effectiveFile ? (
                <span className="text-green-700 font-medium">
                  {isManualOverride ? 'Selected' : 'Matched'}: {effectiveFile.county} County — {formatBytes(effectiveFile.sizeBytes)} — v{effectiveFile.versionDate ?? 'unknown'}
                </span>
              ) : (
                <span className="text-amber-700">
                  No Regrid file available for your jurisdiction.{filesLoading ? ' Searching…' : ''}
                </span>
              )}
            </div>
          ),
        )}
        {renderPathCard(
          'manual',
          <Upload className="h-4 w-4 text-blue-600" />,
          'Manual Upload',
          'Upload your own GeoParquet, GeoJSON, or GPKG file. No tokens required — you manage your own data updates.',
        )}
        {renderPathCard(
          'skip',
          <FileText className="h-4 w-4 text-gray-500" />,
          'Skip for now',
          'No parcel data will be imported during onboarding. You can configure this later in Settings.',
        )}
      </div>

      {/* Regrid path options */}
      {value.path === 'regrid' && (
        <div className="space-y-4">
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
            Your plan includes <strong>1 parcel data update per year</strong> at renewal.
            If you need to update more frequently, set a higher number below.
          </div>

          <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-900">Updates per year</div>
              <div className="text-xs text-gray-500">1 is included at renewal. Set a higher value if the department purchased additional updates.</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => adjustTokens(-1)}
                disabled={(value.tokensPerYear ?? 1) <= 1}
                className="p-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                aria-label="Decrease updates per year"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <input
                type="number"
                min={1}
                max={12}
                value={value.tokensPerYear ?? 1}
                onChange={(e) => handleSetTokens(e.target.value)}
                className="w-14 text-center text-sm font-semibold border border-gray-300 rounded-md py-1"
                aria-label="Updates per year"
              />
              <button
                type="button"
                onClick={() => adjustTokens(1)}
                disabled={(value.tokensPerYear ?? 1) >= 12}
                className="p-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                aria-label="Increase updates per year"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Effective file details — manual selection wins over auto-match */}
          {effectiveFile ? (
            <div className="p-3 border border-green-200 bg-green-50 rounded-lg">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {effectiveFile.county}, {effectiveFile.stateName ?? effectiveFile.state.toUpperCase()}
                  </div>
                  <div className="text-xs text-gray-600">
                    FIPS {effectiveFile.fipsCode} — {formatBytes(effectiveFile.sizeBytes)} — version {effectiveFile.versionDate ?? 'unknown'}
                  </div>
                  <div className="text-xs mt-0.5 text-gray-500">
                    {isManualOverride
                      ? 'Manually selected — overrides the jurisdiction auto-match.'
                      : selectedFile
                        ? 'Selected for this department.'
                        : 'Auto-matched from the Step 1 jurisdiction.'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowBrowseAll((s) => !s)}
                  className="text-xs font-medium text-orange-700 hover:text-orange-800"
                >
                  {showBrowseAll ? 'Hide all files' : 'Browse all files'}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800">
                  <p>No Regrid file is available for your jurisdiction.</p>
                  <p className="mt-1">Please contact support to have your county added, or choose Manual upload / Skip.</p>
                  <button
                    type="button"
                    onClick={() => setShowBrowseAll((s) => !s)}
                    className="mt-2 text-xs font-medium text-orange-700 hover:text-orange-800"
                  >
                    {showBrowseAll ? 'Hide all files' : 'Browse all files'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Browse-all file list */}
          {showBrowseAll && (
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-700">Available Regrid cache files</div>
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {files.map((f) => (
                  <label
                    key={f._id}
                    className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                      value.regridFileId === f._id ? 'bg-orange-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="regrid-cache-file"
                      value={f._id}
                      checked={value.regridFileId === f._id}
                      onChange={() => handleSelectFile(f)}
                      className="h-4 w-4 text-orange-600 border-gray-300 focus:ring-orange-500"
                    />
                    <div className="flex-1 text-xs">
                      <div className="font-medium text-gray-900">
                        {f.county}, {f.state}
                      </div>
                      <div className="text-gray-500">
                        FIPS {f.fipsCode} — {formatBytes(f.sizeBytes)} — version {f.versionDate ?? 'unknown'}
                      </div>
                    </div>
                  </label>
                ))}
                {files.length === 0 && !filesLoading && (
                  <div className="p-3 text-xs text-gray-500">No cache files available.</div>
                )}
              </div>
            </div>
          )}

          {/* Spatial filter */}
          {renderSpatialFilter('Import every parcel in the county', 'Choose which parcels to import from the matched county file.')}

          {/* Owner contact creation policy */}
          <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={value.createOwnerContacts === true}
              onChange={(e) => handleOwnerContactsToggle(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">Create owner contacts from parcel data</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Off by default. When enabled, the import creates one primary-owner contact record per
                imported parcel — for a full county this can be hundreds of thousands of records containing
                owner names and mailing addresses. Leave unchecked to import parcels only; ownership details
                are stored on each parcel either way and contacts can be generated later from
                Settings → Parcel Data.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Manual upload */}
      {value.path === 'manual' && (
        <div className="space-y-4">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-700 font-medium">Drag and drop a GeoParquet, GeoJSON, or GPKG file</p>
            <p className="text-xs text-gray-500 mt-1">or click to browse</p>
            <input
              type="file"
              accept=".geoparquet,.parquet,.geojson,.gpkg"
              onChange={(e) => handleManualFileSelect(e.target.files?.[0])}
              className="mt-3 block w-full text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200"
            />
          </div>
          {value.manualFileName && (
            <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
              <FileText className="h-4 w-4" />
              <span>Selected: {value.manualFileName}</span>
            </div>
          )}

          {/* Filter options also available for manual */}
          {renderSpatialFilter('Import every parcel in the file')}
        </div>
      )}

      {/* Skip path warning */}
      {value.path === 'skip' && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            Locations will be empty. You can configure parcel data later from{' '}
            <strong>Settings → Parcel Data</strong>.
          </div>
        </div>
      )}
    </div>
  );
}
